import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useSocket } from '../lib/socket';
import { useAuthStore } from '../store/auth';
import type {
  AgentPresencePayload,
  LiveCallEndedPayload,
  LiveCallStartedPayload,
} from '@marnic/shared/events';
import type { AgentState, Call, Lead } from '@marnic/shared';
import { formatDuration } from '@marnic/shared';
import { relativeTime } from '../lib/utils';
import { LiveMonitorPanel } from '../components/LiveMonitorPanel';

interface AgentRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  state: AgentState;
  reason: string | null;
  changedAt: string | null;
  skills: string[];
}

interface DashboardStats {
  todaysCalls: number;
  connected: number;
  qualified: number;
  abandoned: number;
  abandonRate: number;
  activeCampaigns: number;
  agentsOnline: number;
}

interface LiveCall extends Call {
  lead: Lead | null;
  agent: { id: string; firstName: string; lastName: string } | null;
  campaign: { id: string; name: string } | null;
}

export function Supervisor(): JSX.Element {
  const qc = useQueryClient();
  const socket = useSocket();
  const accessToken = useAuthStore((s) => s.accessToken);

  const stats = useQuery({
    queryKey: ['supervisor', 'dashboard'],
    queryFn: () => api.get<DashboardStats>('/supervisor/dashboard'),
    refetchInterval: 5000,
  });
  const agents = useQuery({
    queryKey: ['supervisor', 'agents'],
    queryFn: () => api.get<AgentRow[]>('/supervisor/agents'),
    refetchInterval: 5000,
  });
  const liveCalls = useQuery({
    queryKey: ['supervisor', 'live-calls'],
    queryFn: () => api.get<LiveCall[]>('/supervisor/live-calls'),
    refetchInterval: 3000,
  });

  // Real-time presence updates merged into the cache so polling can stay slow.
  useEffect(() => {
    if (!socket || !accessToken) return;

    const onPresence = (p: AgentPresencePayload): void => {
      qc.setQueryData<AgentRow[] | undefined>(['supervisor', 'agents'], (prev) => {
        if (!prev) return prev;
        return prev.map((a) => (a.id === p.agentId ? { ...a, state: p.state, changedAt: p.since } : a));
      });
    };
    const onLiveCallStarted = (_p: LiveCallStartedPayload): void => {
      void qc.invalidateQueries({ queryKey: ['supervisor', 'live-calls'] });
      void qc.invalidateQueries({ queryKey: ['supervisor', 'dashboard'] });
    };
    const onLiveCallEnded = (_p: LiveCallEndedPayload): void => {
      void qc.invalidateQueries({ queryKey: ['supervisor', 'live-calls'] });
      void qc.invalidateQueries({ queryKey: ['supervisor', 'dashboard'] });
    };

    socket.on('agent_presence', onPresence);
    socket.on('live_call_started', onLiveCallStarted);
    socket.on('live_call_ended', onLiveCallEnded);
    return () => {
      socket.off('agent_presence', onPresence);
      socket.off('live_call_started', onLiveCallStarted);
      socket.off('live_call_ended', onLiveCallEnded);
    };
  }, [socket, accessToken, qc]);

  // Connected calls first (newest first), then ones still dialling/ringing.
  const sortedLiveCalls = [...(liveCalls.data ?? [])].sort((a, b) => {
    const ac = a.status === 'in_progress' ? 0 : 1;
    const bc = b.status === 'in_progress' ? 0 : 1;
    return ac - bc || new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
  });

  // Snapshot the call being monitored so the panel keeps its labels after
  // the call drops out of the live list (the captured audio stays replayable).
  const [monitored, setMonitored] = useState<{ id: string; title: string; subtitle: string } | null>(null);
  const monitor = (c: LiveCall): void => {
    const agent = c.agent ? `${c.agent.firstName} ${c.agent.lastName}` : 'Unassigned';
    const other = c.lead ? `${c.lead.firstName} ${c.lead.lastName}` : (c.direction === 'inbound' ? c.fromNumber : c.toNumber);
    setMonitored({
      id: c.id,
      title: `${agent} ↔ ${other}`,
      subtitle: [c.direction === 'inbound' ? 'Inbound' : 'Outbound', c.provider === 'VOIPLINE' ? 'VoIPline' : 'Twilio', c.campaign?.name]
        .filter(Boolean)
        .join(' · '),
    });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Supervisor</h1>
        <p className="text-sm text-ink-500">Live view across active campaigns and agents.</p>
      </header>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Stat label="Calls today" value={stats.data?.todaysCalls ?? 0} />
        <Stat label="Connected" value={stats.data?.connected ?? 0} />
        <Stat label="Qualified" value={stats.data?.qualified ?? 0} />
        <Stat label="Abandoned" value={stats.data?.abandoned ?? 0} />
        <Stat label="Abandon rate" value={`${((stats.data?.abandonRate ?? 0) * 100).toFixed(2)}%`} highlight={(stats.data?.abandonRate ?? 0) > 0.025} />
        <Stat label="Active campaigns" value={stats.data?.activeCampaigns ?? 0} />
        <Stat label="Agents online" value={stats.data?.agentsOnline ?? 0} />
      </section>

      {monitored && (
        <LiveMonitorPanel
          key={monitored.id}
          callId={monitored.id}
          title={monitored.title}
          subtitle={monitored.subtitle}
          onClose={() => setMonitored(null)}
        />
      )}

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="card overflow-hidden">
          <header className="border-b border-ink-200 px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Agents</h2>
          </header>
          <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">State</th>
                <th className="px-4 py-2">Since</th>
              </tr>
            </thead>
            <tbody>
              {agents.data?.map((a) => (
                <tr key={a.id} className="border-t border-ink-100">
                  <td className="px-4 py-2">{a.firstName} {a.lastName}</td>
                  <td className="px-4 py-2">
                    <span className={`badge ${stateBadgeClass(a.state)}`}>{stateLabel(a.state)}</span>
                  </td>
                  <td className="px-4 py-2 text-ink-500">{relativeTime(a.changedAt)}</td>
                </tr>
              ))}
              {agents.data?.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-ink-500">No agents.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div className="card overflow-hidden">
          <header className="border-b border-ink-200 px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Live calls</h2>
          </header>
          <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
              <tr>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Agent</th>
                <th className="px-4 py-2">Counterparty</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sortedLiveCalls.map((c) => {
                const connected = c.status === 'in_progress';
                const elapsed = Math.floor((Date.now() - new Date(c.answeredAt ?? c.startedAt).getTime()) / 1000);
                const isMonitored = monitored?.id === c.id;
                return (
                  <tr key={c.id} className={`border-t border-ink-100 ${isMonitored ? 'bg-brand-50' : ''}`}>
                    <td className="px-4 py-2">
                      {connected ? (
                        <span className="badge gap-1.5 bg-emerald-100 text-emerald-700">
                          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-600" />
                          Connected
                        </span>
                      ) : (
                        <span className="badge bg-amber-100 text-amber-700">
                          {c.direction === 'inbound' ? 'Ringing agent' : 'Dialling'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">{c.agent ? `${c.agent.firstName} ${c.agent.lastName}` : '–'}</td>
                    <td className="px-4 py-2">
                      {c.lead ? `${c.lead.firstName} ${c.lead.lastName}` : (c.direction === 'inbound' ? c.fromNumber : c.toNumber)}
                    </td>
                    <td className="px-4 py-2 font-mono">{formatDuration(elapsed)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        className={`${isMonitored ? 'btn-primary' : 'btn-secondary'} text-xs`}
                        disabled={!connected && !isMonitored}
                        title={connected ? 'Listen to this call' : 'Available once the call is answered'}
                        onClick={() => monitor(c)}
                      >
                        👂 {isMonitored ? 'Listening' : 'Listen'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {sortedLiveCalls.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-500">No calls in progress right now.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function stateBadgeClass(state: AgentState): string {
  switch (state) {
    case 'available': return 'bg-emerald-100 text-emerald-700';
    case 'busy_call': return 'bg-amber-100 text-amber-700';
    case 'wrap_up': return 'bg-purple-100 text-purple-700';
    case 'break':
    case 'lunch':
      return 'bg-blue-100 text-blue-700';
    case 'offline':
      return 'bg-ink-100 text-ink-500';
    default:
      return 'bg-ink-100 text-ink-700';
  }
}

function stateLabel(state: AgentState): string {
  return state.replace('_', ' ');
}

function Stat({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }): JSX.Element {
  return (
    <div className={`card p-3 ${highlight ? 'border-red-300 bg-red-50' : ''}`}>
      <div className="text-xs uppercase tracking-wide text-ink-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${highlight ? 'text-red-700' : ''}`}>{value}</div>
    </div>
  );
}
